export interface ResolvedTerritory {
  country: string;
  region: string;
  label: string;
}

const CACHE_KEY = 'guild_reverse_territory_v1';
const REQUEST_INTERVAL_MS = 1_050;
const memoryCache = new Map<string, ResolvedTerritory>();
const pending = new Map<string, Promise<ResolvedTerritory | null>>();
let requestTail = Promise.resolve();
let lastRequestAt = 0;
let hydrated = false;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function keyFor(lat: number, lng: number) {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

function hydrateCache() {
  if (hydrated) return;
  hydrated = true;
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    for (const [key, value] of Object.entries(parsed)) {
      if (!value || typeof value !== 'object') continue;
      const entry = value as Partial<ResolvedTerritory>;
      if (!entry.country && !entry.region) continue;
      memoryCache.set(key, {
        country: String(entry.country || ''),
        region: String(entry.region || ''),
        label: String(entry.label || ''),
      });
    }
  } catch { /* cache absent ou illisible */ }
}

function persistCache() {
  try {
    // Une notice par coordonnée de donjon : 500 entrées suffisent très
    // largement et empêchent un localStorage sans borne après des années.
    const entries = [...memoryCache.entries()].slice(-500);
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch { /* stockage privé ou saturé */ }
}

function territoryFromPayload(payload: any): ResolvedTerritory | null {
  const address = payload?.address || {};
  const country = String(address.country || '').trim();
  const region = String(
    address.state
      || address.region
      || address.county
      || address.state_district
      || address.municipality
      || address.city
      || '',
  ).trim();
  if (!country && !region) return null;
  return { country, region, label: String(payload?.display_name || '').trim() };
}

async function requestTerritory(lat: number, lng: number): Promise<ResolvedTerritory | null> {
  const wait = Math.max(0, REQUEST_INTERVAL_MS - (Date.now() - lastRequestAt));
  if (wait) await delay(wait);
  lastRequestAt = Date.now();
  const baseUrl = String(import.meta.env.VITE_NOMINATIM_BASE_URL || 'https://nominatim.openstreetmap.org').replace(/\/$/, '');
  const url = new URL(baseUrl + '/reverse');
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('zoom', '10');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  const response = await fetch(url, {
    headers: { 'Accept-Language': 'fr' },
    referrerPolicy: 'strict-origin-when-cross-origin',
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return territoryFromPayload(await response.json());
}

export function reverseTerritoryFromCoordinates(latInput: number, lngInput: number) {
  const lat = Number(latInput);
  const lng = Number(lngInput);
  if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
    return Promise.resolve(null);
  }
  hydrateCache();
  const key = keyFor(lat, lng);
  if (memoryCache.has(key)) return Promise.resolve(memoryCache.get(key)!);
  if (pending.has(key)) return pending.get(key)!;

  const task = requestTail.then(async () => {
    try {
      const territory = await requestTerritory(lat, lng);
      if (territory) {
        memoryCache.set(key, territory);
        persistCache();
      }
      return territory;
    } catch (error) {
      console.warn('[TERRITOIRE] API de géocodage indisponible :', (error as Error)?.message || error);
      return null;
    }
  });
  pending.set(key, task);
  void task.finally(() => pending.delete(key));
  requestTail = task.then(() => undefined, () => undefined);
  return task;
}
